//Built-in libraries
import Card from '@/components/UI/Card';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
//Services
import { useDashboardData } from '@/hooks/ReactQueryHooks/useDashboardQuery';
//Assets and Miscellaneous
import type { DashboardType } from '@/types/Analytics.ts';
import Table from '../../UI/Table';
import { Widget } from '../constants';
//Custom components
import WidgetLayout from '../WidgetLayout';

interface DistrictImpactWidgetProps {
  dashboard: DashboardType;
}

const DistrictImpactWidget = ({ dashboard }: DistrictImpactWidgetProps) => {
  const [perPage, setPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(1);

  const fetchDistrictImpactWidgetData = useDashboardData({
    dashboard,
    widget: Widget.PROJECTS_BY_NEIGHBORHOOD_DISTRICT,
  });

  const districtImpactWidgetData = fetchDistrictImpactWidgetData?.data?.data;

  const totalPage = Math.max(1, Math.ceil((districtImpactWidgetData?.length ?? 0) / perPage));

  const pagedData = useMemo(() => {
    if (!Array.isArray(districtImpactWidgetData)) {
      return [];
    }
    const start = (page - 1) * perPage;
    return districtImpactWidgetData.slice(start, start + perPage);
  }, [districtImpactWidgetData, page, perPage]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPage));
  }, [totalPage]);

  const nextPageHandler = () => {
    setPage((prev) => Math.min(prev + 1, totalPage));
  };

  const previousPageHandler = () => {
    setPage((prev) => Math.max(prev - 1, 1));
  };

  const tableColumns: ColumnDef<any>[] = [
    {
      id: 'name',
      header: () => <div className='uppercase'>District</div>,
      accessorKey: 'name',
      cell: (props) => {
        const { getValue } = props;
        return getValue() as string;
      },
    },
    {
      id: '_count',
      header: () => <div className='text-center uppercase'>Projects</div>,
      accessorKey: '_count',
      cell: (props) => {
        const { getValue } = props;
        return (
          <div className='text-center'>
            <span>{getValue() as number}</span>
          </div>
        );
      },
    },
    {
      id: 'totalDevelopmentCost',
      header: () => <div className='text-center uppercase'>Total Development Value</div>,
      accessorKey: 'totalDevelopmentCost',
      cell: (props) => {
        const { getValue } = props;
        return (
          <div className='text-center'>
            <CurrencyFormatDisplay value={getValue() as number} prefix='$' />
          </div>
        );
      },
    },
  ];

  const tablePagination = {
    page: page - 1,
    pageSize: perPage,
    pageCount: totalPage,
    currentPage: page,
    setPageSize: (v: number) => {
      setPerPage(v);
      setPage(1);
    },
    nextPage: nextPageHandler,
    previousPage: previousPageHandler,
  };

  if (!Array.isArray(districtImpactWidgetData) || districtImpactWidgetData.length === 0) {
    return null;
  }

  return (
    <div className='col-span-4 2xl:col-span-2'>
      <WidgetLayout dashboard={dashboard} widget={Widget.PROJECTS_BY_NEIGHBORHOOD_DISTRICT}>
        <Card title='District Impact'>
          <div className='w-full overflow-x-auto'>
            <Table cols={tableColumns} tableData={pagedData} pagination={tablePagination} />
          </div>
        </Card>
      </WidgetLayout>
    </div>
  );
};

export default DistrictImpactWidget;
